import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Account } from "@faire/web--source/slate/icons/foundation/Account";
import React from "react";

import { SelfServeNodes } from "../../types";
import { AccountReactivationIA } from "../instantAnswers/myAccount-accountReactivation";
import { AccountRejectedIA } from "../instantAnswers/myAccount-accountRejected";
import { BillingAndPayoutIA } from "../instantAnswers/myAccount-billingAndPayout";
import { LoginEmailIA } from "../instantAnswers/myAccount-loginEmail";
import { MultifactorAuthenticationIA } from "../instantAnswers/myAccount-multifactorAuthentication";
import { NotificationsIA } from "../instantAnswers/myAccount-notifications";
import { OnboardingHowFaireWorksIA } from "../instantAnswers/myAccount-onboardingHowFaireWorks";
import { PasswordIA } from "../instantAnswers/myAccount-password";
import { ShipFromAddressIA } from "../instantAnswers/myAccount-shipFromAddress";
import { SubmitRequestedInformationIA } from "../instantAnswers/myAccount-submitRequestedInformation";
import { TaxInformationIA } from "../instantAnswers/myAccount-taxInformation";
import { VerificationStatusIA } from "../instantAnswers/myAccount-verificationStatus";
import { accountReactivationVisibilityFn } from "../visibilityFunctions/accountReactivationVisibilityFn";
import { accountVerificationNotApprovedVisibilityFn } from "../visibilityFunctions/accountVerificationNotApprovedVisibilityFn";
import { accountVerificationNotRejectedVisibilityFn } from "../visibilityFunctions/accountVerificationNotRejectedVisibilityFn";
import { accountVerificationRejectedVisibilityFn } from "../visibilityFunctions/accountVerificationRejectedVisibilityFn";

import { brandActivePeteEventDataFn } from "./peteEventDataFunction/brandActivePeteEventDataFn";

export const useMyAccountBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    MyAccount: {
      id: "MyAccount",
      label: strictLocalize({
        defaultMessage: "My account",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Account fontSize="24px" color="faireBlack" />,
      children: ["UpdateAccountInformation", "LoginCredentials", "Onboarding"],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Account issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    UpdateAccountInformation: {
      id: "UpdateAccountInformation",
      label: strictLocalize({
        defaultMessage: "Update account information",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "BillingAndPayout",
        "Notifications",
        "ShipFromAddress",
        "TaxInformation",
        "AccountReactivation",
      ],
      level: 2,
    },
    LoginCredentials: {
      id: "LoginCredentials",
      label: strictLocalize({
        defaultMessage: "Login credentials",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["MultifactorAuthentication", "LoginEmail", "Password"],
      level: 2,
    },

    Onboarding: {
      id: "Onboarding",
      label: strictLocalize({
        defaultMessage: "Onboarding",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["OnboardingHowFaireWorks", "AccountVerification"],
      additionalPeteEventData: brandActivePeteEventDataFn,
      level: 2,
    },
    BillingAndPayout: {
      id: "BillingAndPayout",
      label: strictLocalize({
        defaultMessage: "Billing and payout",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: BillingAndPayoutIA(strictLocalize),
      level: 3,
    },
    Notifications: {
      id: "Notifications",
      label: strictLocalize({
        defaultMessage: "Notifications",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.EMAIL_PREFERENCES],
      instantAnswer: NotificationsIA(strictLocalize),
      level: 3,
    },
    ShipFromAddress: {
      id: "ShipFromAddress",
      label: strictLocalize({
        defaultMessage: "Ship From Address",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: ShipFromAddressIA(strictLocalize),
      level: 3,
    },
    TaxInformation: {
      id: "TaxInformation",
      label: strictLocalize({
        defaultMessage: "Tax information",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.InvoicesAndBilling.TAX_INFORMATION],
      instantAnswer: TaxInformationIA(strictLocalize),
      level: 3,
    },
    AccountReactivation: {
      id: "AccountReactivation",
      label: strictLocalize({
        defaultMessage: "Account reactivation",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AccountReactivationIA(strictLocalize),
      isNodeVisible: accountReactivationVisibilityFn,
      level: 3,
    },
    MultifactorAuthentication: {
      id: "MultifactorAuthentication",
      label: strictLocalize({
        defaultMessage: "Multifactor authentication",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: MultifactorAuthenticationIA(strictLocalize),
      level: 3,
    },
    LoginEmail: {
      id: "LoginEmail",
      label: strictLocalize({
        defaultMessage: "Login email",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.ACCOUNT_EMAIL],
      instantAnswer: LoginEmailIA(strictLocalize),
      level: 3,
    },
    Password: {
      id: "Password",
      label: strictLocalize({
        defaultMessage: "Password",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Account.RESETTING_PASSWORD],
      instantAnswer: PasswordIA(strictLocalize),
      level: 3,
    },
    OnboardingHowFaireWorks: {
      id: "OnboardingHowFaireWorks",
      label: strictLocalize({
        defaultMessage: "How Faire works",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQForProspectiveBrands.BECOME_BRAND],
      instantAnswer: OnboardingHowFaireWorksIA(strictLocalize),
      level: 3,
    },
    AccountVerification: {
      id: "AccountVerification",
      label: strictLocalize({
        defaultMessage: "Account verification",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "VerificationStatus",
        "SubmitRequestedInformation",
        "AccountRejected",
      ],
      isNodeVisible: accountVerificationNotApprovedVisibilityFn,
      level: 3,
    },
    VerificationStatus: {
      id: "VerificationStatus",
      label: strictLocalize({
        defaultMessage: "Verification status",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: VerificationStatusIA(strictLocalize),
      isNodeVisible: accountVerificationNotRejectedVisibilityFn,
      level: 4,
    },
    SubmitRequestedInformation: {
      id: "SubmitRequestedInformation",
      label: strictLocalize({
        defaultMessage: "Submit requested information",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: SubmitRequestedInformationIA(strictLocalize),
      isNodeVisible: accountVerificationNotRejectedVisibilityFn,
      level: 4,
    },
    AccountRejected: {
      id: "AccountRejected",
      label: strictLocalize({
        defaultMessage: "Account rejected",
        description: {
          text: "A fourth level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: AccountRejectedIA(strictLocalize),
      isNodeVisible: accountVerificationRejectedVisibilityFn,
      level: 4,
    },
  };
};
